import { useState, useEffect, useCallback, useRef } from "react";
import { useBiostreamEvent } from "../use-biostream-event";
import { useStyles, useColors } from "../ui";
import { useCommand, useRpc } from "../store";
import { uuidv7 } from "../../../shared/src/ids";
import { decodeBiostreamPayload } from "../../../shared/src/proto-decode";
import { encodePairConfirm, encodeDiscoverRequest, encodeStatusPoll, encodeMedicInvite } from "../../../shared/src/proto-encode";
import { useFieldEvent } from "../field-event-context";
import { useAuth } from "../auth";
import type { PatientListItem } from "../../../shared/src/protocol";

// ─── Types ────────────────────────────────────────────────────────────

interface BleDevice {
  name: string | null;
  address: string;
  rssi: number;
}

interface UnassignedWatch {
  watchId: string;
  address: string;       // board BLE address
  firmwareVersion?: string;
  seenAt: number;
}

interface PairedWatch {
  watchId: string;
  soldierName: string;
  soldierId: string;
  pairedAt: number;
}

// NFC scan is a two-stage flow: watch tap → dogtag tap
type NfcStage =
  | { stage: "watch" }                          // waiting for watch NFC
  | { stage: "dogtag"; watchId: string }        // watch scanned, waiting for dogtag

// ─── Bridge helpers ───────────────────────────────────────────────────

function getBridge() { return (window as any).DataLab ?? null; }
function bridgeStartNfc() { getBridge()?.startNfcScan?.(); }
function bridgeStopNfc()  { getBridge()?.stopNfcScan?.(); }
function bridgeStartScan() { getBridge()?.startBleScan?.(); }
function bridgeStopScan()  { getBridge()?.stopBleScan?.(); }
function bridgeGetDevices(): BleDevice[] {
  try { const raw = getBridge()?.getBleDevices?.(); return raw ? JSON.parse(raw) : []; }
  catch { return []; }
}
function bridgeWriteGatt(address: string, hex: string) {
  getBridge()?.writeGatt?.(address, hex);
}

function upsertUnassigned(prev: UnassignedWatch[], entry: UnassignedWatch): UnassignedWatch[] {
  const i = prev.findIndex((w) => w.watchId === entry.watchId);
  if (i >= 0) { const next = [...prev]; next[i] = entry; return next; }
  return [...prev, entry];
}

// ─── NFC scan overlay ─────────────────────────────────────────────────

function NfcScanOverlay({
  stage, onCancel,
}: {
  stage: NfcStage;
  onCancel: () => void;
}) {
  const colors = useColors();
  const styles = useStyles();

  const isWatch = stage.stage === "watch";

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200,
    }}>
      <div style={{
        background: colors.bg, border: `1px solid ${colors.border}`,
        width: "min(360px, 90vw)", padding: "32px 24px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: "16px",
        textAlign: "center",
      }}>
        <div style={{ fontSize: "3rem" }}>{isWatch ? "⌚" : "🪖"}</div>
        <div style={{ fontWeight: 700, fontSize: "1.1rem", color: colors.text }}>
          {isWatch ? "Tap phone to watch" : "Tap phone to dogtag"}
        </div>
        <div style={{ fontSize: "0.875rem", color: colors.textMuted }}>
          {isWatch
            ? "Hold the phone near the NFC tag on the watch"
            : "Hold the phone near the soldier's dogtag"}
        </div>
        {!isWatch && (
          <div style={{
            fontSize: "0.8rem", color: colors.textMuted,
            fontFamily: "monospace", background: colors.bgAlt,
            padding: "4px 8px",
          }}>
            Watch: {(stage as { stage: "dogtag"; watchId: string }).watchId}
          </div>
        )}
        <button style={styles.btn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Soldier picker modal ─────────────────────────────────────────────

function SoldierPicker({
  onSelect, onCancel, assignedSoldierIds,
}: {
  onSelect: (id: string, name: string) => void;
  onCancel: () => void;
  assignedSoldierIds: Set<string>;
}) {
  const styles = useStyles();
  const colors = useColors();
  const rpc = useRpc();

  const [search, setSearch] = useState("");
  const [results, setResults] = useState<PatientListItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await rpc<{ patients: PatientListItem[] }>({
          type: "listPatients",
          search: search || undefined,
          sortKey: "name", sortDir: "asc", page: 1, limit: 20,
        });
        if (!cancelled) setResults(res.patients.filter((p) => !assignedSoldierIds.has(p.id)));
      } catch { /* offline */ }
      finally { if (!cancelled) setLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search, rpc, assignedSoldierIds]);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
    }}>
      <div style={{
        background: colors.bg, border: `1px solid ${colors.border}`,
        width: "min(480px, 90vw)", maxHeight: "70vh",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ ...styles.toolbar, borderBottom: `1px solid ${colors.border}` }}>
          <span style={{ fontWeight: 600 }}>Assign soldier</span>
          <span style={{ flex: 1 }} />
          <button onClick={onCancel} style={styles.btn}>Cancel</button>
        </div>
        <div style={{ padding: "8px" }}>
          <input
            placeholder="Search soldiers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.input}
          />
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {loading && (
            <div style={{ padding: "12px", textAlign: "center", color: colors.textMuted, fontSize: "0.875rem" }}>
              Loading…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div style={{ padding: "12px", textAlign: "center", color: colors.textMuted, fontSize: "0.875rem" }}>
              No soldiers found
            </div>
          )}
          {results.map((p) => (
            <div
              key={p.id}
              onClick={() => onSelect(p.id, p.name ?? p.id)}
              style={{
                padding: "10px 16px", borderBottom: `1px solid ${colors.border}`,
                cursor: "pointer", fontSize: "0.875rem",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = colors.bgAlt)}
              onMouseOut={(e) => (e.currentTarget.style.background = "")}
            >
              <div style={{ fontWeight: 600 }}>{p.name ?? "—"}</div>
              <div style={{ fontSize: "0.75rem", color: colors.textMuted }}>
                {[p.militaryRank, p.unitName].filter(Boolean).join(" · ")}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── BLE scan panel ───────────────────────────────────────────────────

function BleScanPanel({
  devices, scanning, connectedAddress, onStartScan, onStopScan, onConnect,
}: {
  devices: BleDevice[];
  scanning: boolean;
  connectedAddress: string;
  onStartScan: () => void;
  onStopScan: () => void;
  onConnect: (device: BleDevice) => void;
}) {
  const colors = useColors();
  const styles = useStyles();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>Emulator board</span>
        <span style={{ flex: 1 }} />
        {scanning
          ? <button onClick={onStopScan} style={styles.btn}>Stop</button>
          : <button onClick={onStartScan} style={styles.btnPrimary}>Scan</button>}
      </div>
      <div style={{ border: `1px solid ${colors.border}`, minHeight: "80px" }}>
        {devices.length === 0 ? (
          <div style={{ padding: "16px", textAlign: "center", color: colors.textMuted, fontSize: "0.875rem" }}>
            {scanning ? "Scanning…" : "Scan to find the emulator board"}
          </div>
        ) : devices.map((d) => {
          const connected = d.address === connectedAddress;
          return (
            <div
              key={`${d.name ?? ""}|${d.address}`}
              onClick={() => onConnect(d)}
              style={{
                padding: "10px 12px",
                borderBottom: `1px solid ${colors.border}`,
                cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                fontSize: "0.875rem",
                background: connected ? colors.bgAlt : undefined,
                borderLeft: `3px solid ${connected ? colors.primary : "transparent"}`,
              }}
              onMouseOver={(e) => { if (!connected) e.currentTarget.style.background = colors.bgAlt; }}
              onMouseOut={(e) => { if (!connected) e.currentTarget.style.background = ""; }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>{d.name ?? "(unnamed)"}</div>
                <div style={{ fontSize: "0.75rem", color: colors.textMuted, fontFamily: "monospace" }}>{d.address}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "0.75rem", color: colors.textMuted }}>{d.rssi} dBm</div>
                {connected && <div style={{ fontSize: "0.7rem", color: colors.primary }}>connected</div>}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: "0.75rem", color: colors.textMuted }}>
        Click a device to connect and discover virtual watches
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────

export function DeviceSetupPage() {
  const dispatch = useCommand();
  const rpc = useRpc();
  const styles = useStyles();
  const colors = useColors();
  const {
    fieldEventId, setFieldEventId,
    boardAddress, setBoardAddress,
    pairings, registerPairing,
    loraSessionActive, setLoraSessionActive,
  } = useFieldEvent();
  const { user } = useAuth();

  // BLE scan state
  const [bleDevices, setBleDevices] = useState<BleDevice[]>([]);
  const [scanning, setScanning] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Watch lists
  const [unassignedWatches, setUnassignedWatches] = useState<UnassignedWatch[]>([]);

  // Derive paired watches directly from context — survives navigation
  const pairedWatches: PairedWatch[] = Array.from(pairings.values()).map((p) => ({
    watchId: p.watchId,
    soldierId: p.soldierId,
    soldierName: p.soldierName,
    pairedAt: 0,
  }));

  // NFC flow state — null when idle
  const [nfcStage, setNfcStage] = useState<NfcStage | null>(null);

  // Soldier picker — which watchId we're assigning via manual search
  const [pickerWatchId, setPickerWatchId] = useState<string | null>(null);

  const [status, setStatus] = useState<string | null>(null);

  // Debug log — shows raw BLE events so we can see what's actually arriving
  const [bleLog, setBleLog] = useState<string[]>([]);
  const addBleLog = useCallback((msg: string) => {
    setBleLog((prev) => [msg, ...prev].slice(0, 50));
  }, []);


  // ── BLE scanning — live poll every 1 s, auto-stops after 5 s ──
  const stopScan = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (stopRef.current) { clearTimeout(stopRef.current); stopRef.current = null; }
    bridgeStopScan();
    setScanning(false);
    setBleDevices(bridgeGetDevices());
  }, []);

  const handleStartScan = useCallback(() => {
    setBleDevices([]);
    bridgeStartScan();
    setScanning(true);
    pollRef.current = setInterval(() => setBleDevices(bridgeGetDevices()), 500);
    stopRef.current = setTimeout(() => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      stopRef.current = null;
      bridgeStopScan();
      setScanning(false);
      setBleDevices(bridgeGetDevices());
    }, 5000);
  }, []);

  const handleStopScan = stopScan;

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (stopRef.current) clearTimeout(stopRef.current);
    bridgeStopScan();
  }, []);

  // ── Connect to board ──
  // writeGatt handles the full connect → service discovery → write → enable notifications
  // in one shot, so we don't call connectWatch separately (avoids double-connection).
  const handleConnectBoard = useCallback((device: BleDevice) => {
    setBoardAddress(device.address);
    const hex = encodeDiscoverRequest();
    addBleLog(`→ Connect+DiscoverRequest to ${device.address.slice(-5)} hex=${hex}`);
    setStatus(`Connecting to ${device.name ?? device.address}…`);
    // Send DiscoverRequest immediately — writeGatt will connect then write
    bridgeWriteGatt(device.address, hex);
  }, [setBoardAddress, addBleLog]);

  // ── Re-send DiscoverRequest on an already-connected board ──
  const handleDiscover = useCallback(() => {
    if (!boardAddress) return;
    const hex = encodeDiscoverRequest();
    addBleLog(`→ DiscoverRequest to ${boardAddress.slice(-5)} hex=${hex}`);
    setStatus("Requesting watch list…");
    bridgeWriteGatt(boardAddress, hex);
  }, [boardAddress, addBleLog]);

  // ── Java-side debug events ──
  useBiostreamEvent<{ msg: string }>(
    "biostream:ble-debug",
    ({ msg }) => addBleLog(`[dbg] ${msg}`),
    [addBleLog],
  );

  // ── Inbound BLE data — discover responses / pair requests ──
  useBiostreamEvent<{ address: string; name: string | null; hex: string }>(
    "biostream:ble-data",
    ({ address, name, hex }) => {
      const decoded = decodeBiostreamPayload(hex);
      const shortAddr = address.slice(-5);
      addBleLog(`[${shortAddr}] type=${decoded?.type ?? "decode-fail"} watchId=${decoded?.watchId || "(none)"}`);

      if (!decoded) return;

      if (decoded.type === "pair_request") {
        const watchId = decoded.watchId || [name, address].filter(Boolean).join("|");
        setUnassignedWatches((prev) => upsertUnassigned(prev, { watchId, address, seenAt: Date.now() }));
        setStatus(null);
      }

      if (decoded.type === "discover_response") {
        const watchId = decoded.watchId || [name, address].filter(Boolean).join("|");
        setUnassignedWatches((prev) => upsertUnassigned(prev, {
          watchId,
          address,
          firmwareVersion: decoded.firmwareVersion || undefined,
          seenAt: Date.now(),
        }));
        setStatus(null);
      }
    },
    [addBleLog],
  );

  // ── NFC flow ──
  const startNfcWatchFlow = useCallback(() => {
    setNfcStage({ stage: "watch" });
    bridgeStartNfc();
  }, []);

  const cancelNfcFlow = useCallback(() => {
    bridgeStopNfc();
    setNfcStage(null);
  }, []);

  // Handle incoming NFC reads — drives the two-stage flow.
  // nfcStage=null means the flow is inactive; the hook short-circuits via the deps array.
  useBiostreamEvent<{ payload: string }>(
    "biostream:nfc-scan",
    async ({ payload }) => {
      if (!payload || !nfcStage) return;

      if (nfcStage.stage === "watch") {
        // First tap: watch FICR UID — advance to dogtag stage
        setNfcStage({ stage: "dogtag", watchId: payload });
      } else {
        // Second tap: dogtag payload → patient UUID → look up name then pair
        const watchId = nfcStage.watchId;
        bridgeStopNfc();
        setNfcStage(null);
        setStatus("Looking up soldier…");
        try {
          const res = await rpc<{ patients: PatientListItem[] }>({
            type: "listPatients", search: payload,
            sortKey: "name", sortDir: "asc", page: 1, limit: 5,
          });
          // Find exact UUID match first, fall back to first result
          const match = res.patients.find((p) => p.id === payload) ?? res.patients[0];
          if (match) {
            doPair(watchId, match.id, match.name ?? match.id);
          } else {
            // Dogtag ID not in system — open picker so medic can select manually
            setStatus("Soldier not found — select manually");
            setPickerWatchId(watchId);
          }
        } catch {
          setStatus("Lookup failed — select manually");
          setPickerWatchId(watchId);
        }
      }
    },
    [nfcStage], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // NFC scan on a specific unassigned watch (dogtag only)
  const startNfcDogtag = useCallback((watchId: string) => {
    setNfcStage({ stage: "dogtag", watchId });
    bridgeStartNfc();
  }, []);

  // ── Core pairing logic ──
  const doPair = useCallback(async (
    watchId: string, soldierId: string, soldierName: string,
  ) => {
    setStatus("Pairing…");
    try {
      let evtId = fieldEventId;
      if (!evtId) {
        evtId = uuidv7();
        await dispatch("fieldEvent.create", { uuid: evtId, unitUuid: "" });
        setFieldEventId(evtId);
      }

      await dispatch("watchEvent.pair", {
        uuid: uuidv7(),
        fieldEventUuid: evtId,
        watchId,
        soldierUuid: soldierId,
        firmwareVersion: "",
        timestamp: Date.now(),
      });

      await dispatch("patient.setLastUsedWatch", { uuid: soldierId, watchId });

      bridgeWriteGatt(boardAddress, encodePairConfirm(watchId));
      bridgeWriteGatt(boardAddress, encodeStatusPoll(watchId));

      registerPairing({ watchId, soldierId, soldierName });

      setUnassignedWatches((prev) => prev.filter((w) => w.watchId !== watchId));
      setStatus(`Paired ${watchId} → ${soldierName}`);
    } catch (e) {
      setStatus(`Pairing failed: ${(e as Error).message}`);
    }
  }, [fieldEventId, boardAddress, dispatch, setFieldEventId, registerPairing]);

  // Manual soldier picker confirm
  const handlePickerSelect = useCallback((soldierId: string, soldierName: string) => {
    const watchId = pickerWatchId!;
    setPickerWatchId(null);
    doPair(watchId, soldierId, soldierName);
  }, [pickerWatchId, doPair]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <span style={{ fontWeight: 600 }}>Device Setup</span>
        {fieldEventId && (
          <span style={{ fontSize: "0.75rem", color: colors.textMuted, fontFamily: "monospace" }}>
            Event: {fieldEventId.slice(0, 8)}
          </span>
        )}
        {boardAddress && (
          <span style={{ fontSize: "0.75rem", color: "#27ae60" }}>
            Board connected
          </span>
        )}
        <span style={{ flex: 1 }} />
        {/* NFC flow button — only when NFC is available (inside ATAK) */}
        <button style={styles.btnPrimary} onClick={startNfcWatchFlow}>
          Add watch (NFC)
        </button>
      </div>

      {/* NFC overlay */}
      {nfcStage && (
        <NfcScanOverlay stage={nfcStage} onCancel={cancelNfcFlow} />
      )}

      {/* Soldier picker */}
      {pickerWatchId && (
        <SoldierPicker
          onSelect={handlePickerSelect}
          onCancel={() => setPickerWatchId(null)}
          assignedSoldierIds={new Set(Array.from(pairings.values()).map((p) => p.soldierId))}
        />
      )}

      <div style={{ flex: 1, overflow: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: "24px" }}>

        {/* Status banner */}
        {status && (
          <div style={{
            padding: "10px 12px", background: colors.bgAlt,
            border: `1px solid ${colors.border}`, fontSize: "0.875rem",
            color: status.startsWith("Pairing failed") ? "#c0392b" : colors.text,
          }}>
            {status}
          </div>
        )}

        {/* ── BLE event log ─────────────────────────────────────────────── */}
        {bleLog.length > 0 && (
          <section>
            <div style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: colors.textMuted, marginBottom: "4px" }}>
              BLE log
              <button style={{ ...styles.btn, marginLeft: "8px", fontSize: "0.7rem", padding: "1px 6px" }} onClick={() => setBleLog([])}>clear</button>
            </div>
            <div style={{
              fontFamily: "monospace", fontSize: "0.7rem", color: colors.textMuted,
              background: colors.bgAlt, border: `1px solid ${colors.border}`,
              padding: "6px 8px", maxHeight: "120px", overflow: "auto",
            }}>
              {bleLog.map((line, i) => <div key={i}>{line}</div>)}
            </div>
          </section>
        )}

        {/* ── Emulator section ─────────────────────────────────────────── */}
        <section>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: colors.textMuted, marginBottom: "8px" }}>
            Emulator / board
          </div>
            <BleScanPanel
            devices={bleDevices}
            scanning={scanning}
            connectedAddress={boardAddress}
            onStartScan={handleStartScan}
            onStopScan={handleStopScan}
            onConnect={handleConnectBoard}
          />
          {boardAddress && (
            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
              <button style={{ ...styles.btnPrimary, alignSelf: "flex-start" }} onClick={handleDiscover}>
                Get watches
              </button>
              {fieldEventId && (
                <button
                  style={{ ...styles.btn, alignSelf: "flex-start", color: loraSessionActive ? "#27ae60" : undefined }}
                  onClick={() => {
                    if (loraSessionActive) {
                      setLoraSessionActive(false);
                      setStatus("LoRa medic session stopped");
                    } else {
                      bridgeWriteGatt(boardAddress, encodeMedicInvite(
                        fieldEventId,
                        user?.name ?? "Host",
                        user?.userId ?? "",
                      ));
                      setLoraSessionActive(true);
                      setStatus("LoRa invite sent — relaying watch events");
                    }
                  }}
                >
                  {loraSessionActive ? "LoRa active ✓" : "Connect local medic"}
                </button>
              )}
            </div>
          )}
        </section>

        {/* ── Unassigned watches ────────────────────────────────────────── */}
        {unassignedWatches.length > 0 && (
          <section>
            <div style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: colors.primary, marginBottom: "8px" }}>
              Watches discovered ({unassignedWatches.length})
            </div>
            <div style={{ border: `1px solid ${colors.border}` }}>
              {unassignedWatches.map((w) => (
                <div
                  key={w.watchId}
                  style={{
                    padding: "10px 12px", borderBottom: `1px solid ${colors.border}`,
                    display: "flex", alignItems: "center", gap: "12px", fontSize: "0.875rem",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontFamily: "monospace" }}>{w.watchId}</div>
                    <div style={{ fontSize: "0.75rem", color: colors.textMuted }}>
                      {w.firmwareVersion && <span style={{ marginRight: "8px" }}>{w.firmwareVersion}</span>}
                      {new Date(w.seenAt).toLocaleTimeString()}
                    </div>
                  </div>
                  <button style={styles.btn} onClick={() => startNfcDogtag(w.watchId)}>
                    Scan dogtag
                  </button>
                  <button style={styles.btnPrimary} onClick={() => setPickerWatchId(w.watchId)}>
                    Assign soldier
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Paired this session ────────────────────────────────────────── */}
        <section>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: colors.textMuted, marginBottom: "8px" }}>
            Paired this session
          </div>
          <table style={{ ...styles.table, width: "100%" }}>
            <thead>
              <tr>
                <th style={styles.th}>Watch ID</th>
                <th style={styles.th}>Soldier</th>
                <th style={styles.th}>Paired at</th>
                <th style={styles.th} />
              </tr>
            </thead>
            <tbody>
              {pairedWatches.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ ...styles.td, color: colors.textMuted, textAlign: "center" }}>
                    No watches paired yet
                  </td>
                </tr>
              )}
              {pairedWatches.map((w) => (
                <tr key={w.watchId}>
                  <td style={styles.td}><span style={{ fontFamily: "monospace" }}>{w.watchId}</span></td>
                  <td style={styles.td}>{w.soldierName}</td>
                  <td style={styles.td}>{new Date(w.pairedAt).toLocaleTimeString()}</td>
                  <td style={{ ...styles.td, textAlign: "right" }}>
                    <button style={styles.btn} onClick={() => {
                      bridgeWriteGatt(boardAddress, encodePairConfirm(w.watchId));
                      setStatus(`Re-paired ${w.watchId}`);
                    }}>
                      Re-pair
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
